import { Button } from '@customerio/pluma-components/react';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { useCopilotSessionContext } from '@/features/copilot/components/session-provider';
import { useShouldShowAgentBar } from '../../hooks/use-agent-fullscreen';
import { useIsAgentLauncherSuppressed } from '../../hooks/use-suppress-agent-launcher';
import { useSidebarActive } from '@/features/sidebar';
import { useCopilotSidebar } from '@/features/copilot/hooks/use-copilot-sidebar';
import { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { useCallback } from 'react';
import { useSuggestions, type ResolvedSuggestion } from '../suggestions';
import { AnimatedSuggestionChips } from '../suggestions/animated-suggestion-chips';
import { useSuggestionImpressionTracking } from '../suggestions/impression';
import { AskAgentSparkleIcon } from '../ask-agent-sparkle-icon';
import * as styles from './index.css';

type AskAssistantButtonProps = { onClick: () => void };

function AskAssistantButton({ onClick }: AskAssistantButtonProps) {
  return (
    <GradientRing radius={4}>
      <Button variant="secondary" size="sm" className={gradientHoverTarget} onClick={onClick}>
        <AskAgentSparkleIcon />
        Ask the Agent
      </Button>
    </GradientRing>
  );
}

export function AssistantFooter() {
  const isCopilotSidebarOpen = useSidebarActive('copilot');
  const shouldShowAgentBar = useShouldShowAgentBar();
  const session = useCopilotSessionContext();
  const suggestionState = useSuggestions(session);
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const themeContext = useThemeContext();
  const isSuppressed = useIsAgentLauncherSuppressed();

  const isHidden = isCopilotSidebarOpen || !shouldShowAgentBar || isSuppressed;

  // Publishes the footer's height, which grows when the suggestions wrap, so the embedded
  // apps keep their content clear of it. Back to 0px once the footer is gone.
  const footerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element == null) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        const height = entry?.borderBoxSize[0]?.blockSize;
        if (height != null) {
          themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, `${height}px`);
        }
      });
      observer.observe(element);

      return () => {
        observer.disconnect();
        themeContext.setVariable(GlobalCSSVariable.AssistantFooterHeight, '0px');
      };
    },
    [themeContext],
  );

  useSuggestionImpressionTracking(suggestionState, { isHidden, clickLocation: 'footer' });

  if (isHidden) {
    return null;
  }

  const handleSuggestionClick = async (message: string, index: number, suggestion: ResolvedSuggestion) => {
    try {
      openCopilot({ triggerSource: 'footer_suggestion' });
      await session.sendMessage(message);
      track('aiAssistant', 'suggestionClicked', {
        Index: index,
        'Click Location': 'footer',
        'Conversation ID': session.id,
        'Suggestion Label': suggestion.label,
        'Suggestion Group': suggestion.group ?? 'none',
        'Route Group': suggestionState.routeGroup,
        'Suggestion Source': suggestionState.source,
      });
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  const handleAskAssistant = () => {
    openCopilot({ triggerSource: 'footer' });
  };

  return (
    <div ref={footerRef} className={styles.footer}>
      <div className={styles.suggestionsContainer}>
        <AnimatedSuggestionChips state={suggestionState} onSuggestionClick={handleSuggestionClick} />
      </div>
      <div className={styles.actionsContainer}>
        <AskAssistantButton onClick={handleAskAssistant} />
      </div>
    </div>
  );
}
